import type { FC } from 'react';
import { useState } from 'react';
import Button from '@components/Button';
import { Show, ShowFirstMatching } from '@components/Show';
import { useBlockActions, useBlockEditFormStore } from '@ContractBuilder/modules/block-edit';
import { useUIStore } from '@ContractBuilder/store/ui.store';
import { mdiCircleOffOutline, mdiRestore } from '@mdi/js';
import Icon from '@mdi/react';
import { isBlockInactive } from '@root/helpers/blocks';
import { isBlocksPath } from '@root/src/utils/app-paths';

import { DeactivateBlockConfirmationModal } from './DeactivateBlockConfirmationModal';

interface LibraryBlockActionButtonsProps {
  hasChanges?: boolean;
  onUpdate: () => Promise<void> | void;
}

export const LibraryBlockActionButtons: FC<LibraryBlockActionButtonsProps> = ({ hasChanges, onUpdate }) => {
  const isBlock = isBlocksPath();
  const [isDeactivateBlockConfirmationModalOpen, setIsDeactivateBlockConfirmationModalOpen] = useState(false);

  const { formValues: state, currentBlock } = useBlockEditFormStore(({ formValues, currentBlock }) => ({
    formValues,
    currentBlock,
  }));
  const activeEditingField = useUIStore(({ activeEditingField }) => activeEditingField);
  const { restoreBlockInLibrary, deactivateBlockInLibrary, reactivateBlockInLibrary } = useBlockActions();

  const handleAction = async (action: (blockId: string) => Promise<void>) => {
    if (state?.id) {
      await action(state.id);
      await onUpdate();
      setIsDeactivateBlockConfirmationModalOpen(false);
    }
  };

  const handleRestoreBlockInLibrary = () => handleAction(restoreBlockInLibrary);
  const handleDeactivateBlockInLibrary = () => handleAction(deactivateBlockInLibrary);
  const handleReactivateBlockInLibrary = () => handleAction(reactivateBlockInLibrary);

  if (!state) {
    return null;
  }

  const isInactive = isBlockInactive(currentBlock?.inception_date, currentBlock?.expiry_date);

  return (
    <>
      <DeactivateBlockConfirmationModal
        isBlock={isBlock}
        isOpen={isDeactivateBlockConfirmationModalOpen}
        onCancel={() => setIsDeactivateBlockConfirmationModalOpen(false)}
        onSaveChanges={handleDeactivateBlockInLibrary}
        usedInTemplates={currentBlock?.usedInTemplates}
      />
      <ShowFirstMatching>
        <Show when={!!state?.deleted_at}>
          <Button className="h-[34px]" kind="green" onClick={handleRestoreBlockInLibrary}>
            <Icon path={mdiRestore} size={0.8} />
            Restore
          </Button>
        </Show>
        <Show when={isInactive}>
          <Button className="h-[34px]" kind="green" onClick={handleReactivateBlockInLibrary}>
            <Icon path={mdiRestore} size={0.8} />
            Reactivate
          </Button>
        </Show>
        <Show when={!isInactive}>
          <Button
            className="h-[34px]"
            kind="danger"
            isDisabled={hasChanges || !!activeEditingField}
            onClick={() => setIsDeactivateBlockConfirmationModalOpen(true)}
          >
            <Icon path={mdiCircleOffOutline} size={0.8} />
            Deactivate
          </Button>
        </Show>
      </ShowFirstMatching>
    </>
  );
};
